/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Autocomplete, CircularProgress, InputAdornment, TextField, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BaseInput from 'components/common/BaseInput';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ReactGA from 'react-ga4';
import { useEffect, useState } from 'react';
import { FaTrash } from 'react-icons/fa';
import { useParams } from 'react-router-dom';

import AddEntryFooter from '../AddEntryFooter';
import * as S from '../Forms.styled';
import FormBox from '../../common/FormBox/FormBox';
import { RootState } from 'redux/store';
import ToggleButtonGroup from '../../common/ToggleButtonGroup/ToggleButtonGroup';
import PartnerInfoSwitch from './PartnerInfoSwitch';
import { EARLIEST_EDUCATION_DATE } from '../../../data/constants/constants';
import { formCounterSlice } from '../../../redux/formCounter';
import { Spouse } from './GeneralInfo';
import { useAuth } from 'contexts/ProtectedRoute';
import { isSalesPersonRole } from 'data/constants/roles';
import { formatDateToYMD, isFullDateFormat, parseStoredDate } from 'utils/fullDateFormat';
import api from 'api';
import { fetchOptionsReturn } from './utils';
import useEnabledCountries from 'hooks/useEnabledCountries';
import {
    enabledMarketFields,
    fieldOfStudyValidationMessages,
    rowCountryEnabled,
    visibleFieldOfStudyMarkets,
    type FieldOfStudyIdKey,
} from 'utils/countries';
import { startOfDay } from 'date-fns';
import languages from '../../../data/selectInputOptions/languageList.json';
import Button from 'views/Dashboards-B2B/components/Button';
import { ReactComponent as AIIcon } from '../../../assets/icons/AI.svg';
import AIRecommendedFieldOfStudy from './AIRecommendedFieldOfStudy';

const LANGUAGES = Object.values(languages).map((item) => item.name);

interface Entry {
    start: Date | null;
    end: Date | null;
    current: boolean | undefined;
    country: string | null;
    region: string | null;
    zip: string | null;
    field_of_study_au_id: number | null;
    field_of_study_ca_id: string | null;
    field_of_study_uk_id: string | null;
    type: string | null;
    completed: boolean | undefined;
    language: string | null;
    institution_name: string | null;
}

const initEntry: Entry = {
    start: null,
    end: null,
    current: undefined,
    field_of_study_au_id: null,
    field_of_study_ca_id: null,
    field_of_study_uk_id: null,
    type: '',
    country: '',
    region: '',
    zip: '',
    completed: undefined,
    language: '',
    institution_name: '',
};

const entryKeys: (keyof Entry)[] = [
    'start',
    'end',
    'current',
    'country',
    'region',
    'zip',
    'field_of_study_au_id',
    'field_of_study_ca_id',
    'field_of_study_uk_id',
    'type',
    'completed',
    'language',
    'institution_name',
];

interface Msg {
    start: string;
    end: string;
    current: string;
    field_of_study_au_id: string;
    field_of_study_ca_id: string;
    field_of_study_uk_id: string;
    type: string;
    country: string;
    region: string;
    zip: string;
    completed: string;
    language: string;
    institution_name: string;
}

const initMsg: Msg = {
    start: '',
    end: '',
    current: '',
    country: '',
    region: '',
    zip: '',
    field_of_study_au_id: '',
    field_of_study_ca_id: '',
    field_of_study_uk_id: '',
    type: '',
    completed: '',
    language: '',
    institution_name: '',
};

type FieldOfStudy = {
    name: string;
    id: number;
    country_code: string;
    code_type?: string;
};

const FIELD_OF_STUDY_ID_KEYS = [
    'field_of_study_au_id',
    'field_of_study_ca_id',
    'field_of_study_uk_id',
] as const;

const isFieldOfStudyIdKey = (key: string): key is FieldOfStudyIdKey =>
    (FIELD_OF_STUDY_ID_KEYS as readonly string[]).includes(key);

const isEmptyFieldValue = (value: unknown) => value === '' || value === undefined || value === null;

export default function Education() {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const { codes, hasSelfServeMarket, has } = useEnabledCountries();
    const tableName = 'EducationNew';
    const [data, setData] = useState<Entry[]>([initEntry]);
    const [validationMsg, setValidationMsg] = useState<Msg[]>([initMsg]);
    const [disableFieldOfStudy, setDisableFieldOfStudy] = useState<boolean[]>([false]);

    const { setShowSpouse } = formCounterSlice.actions;
    const dispatch = useDispatch();

    const [isUserData, setIsUserData] = useState<boolean>(false);
    const [isSpouseInfo, setIsSpouseInfo] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const [options, setOptions] = useState({
        educationOptions: [''],
        countryOptions: [''],
        regionOptions: {} as { [key: string]: string[] },
        fieldOfOptionsAU: [] as FieldOfStudy[],
        fieldOfOptionsCA: [] as FieldOfStudy[],
        fieldOfOptionsUK: [] as FieldOfStudy[],
    });

    useEffect(() => {
        loadOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [codes]);

    useEffect(() => {
        if (isSpouseInfo && isUserData) {
            setIsLoading(false);
        }
    }, [isSpouseInfo, isUserData]);

    const filterValidEntries = (data: any[]): Entry[] => {
        return data.map((item) => {
            const filteredItem = Object.keys(item)
                .filter((key): key is keyof Entry => entryKeys.includes(key as keyof Entry)) // Narrow the key type
                .reduce(
                    (obj, key) => {
                        obj[key] = item[key] as Entry[keyof Entry];
                        return obj;
                    },
                    {} as Record<keyof Entry, Entry[keyof Entry]>
                );

            return filteredItem as Entry;
        });
    };

    const loadData = (data: Entry[]) => {
        const validEntries = filterValidEntries(data);

        if (validEntries && validEntries.length | Object.keys(validEntries).length) {
            const tmp = new Array(validEntries.length).fill(initMsg);
            setValidationMsg(tmp);
            setData(validEntries);

            if (validEntries.length > 0) {
                const tempDisableFieldOfStudy = new Array(validEntries.length).fill([false]);
                setDisableFieldOfStudy(tempDisableFieldOfStudy);
                for (let i = 0; i < validEntries.length; i++) {
                    if (validEntries[i].type === 'High school') {
                        tempDisableFieldOfStudy[i] = true;
                        setDisableFieldOfStudy(tempDisableFieldOfStudy);
                    } else {
                        tempDisableFieldOfStudy[i] = false;
                        setDisableFieldOfStudy(tempDisableFieldOfStudy);
                    }
                }
            }
        }
        if (validEntries == null || validEntries) {
            setIsUserData(true);
        } else {
            setIsUserData(false);
        }
    };
    const fetchOptions = async (
        fetchFunction: () => Promise<any>,
        setFunction: (data: any) => void
    ) => {
        try {
            const resp = await fetchFunction();
            if (resp?.status === 200 || resp?.status === 201) {
                if (resp?.data) {
                    setFunction(resp.data);
                }
            }
        } catch (err) {
            console.log(err);
        }
    };

    const loadOptions = async () => {
        const markets = enabledMarketFields(codes);
        const [educationnes, regions, countries, ...fieldLists] = await Promise.all([
            fetchOptionsReturn(api?.assessment_options.getEducationOptions),
            fetchOptionsReturn(api?.assessment_options.getRegionOptions),
            fetchOptionsReturn(api?.assessment_options.getCountryOptions),
            ...markets.map((market) =>
                fetchOptionsReturn(() =>
                    api?.assessment_options.getFieldOfStudyOptions(market.code)
                )
            ),
        ]);
        const fieldsByCode: Record<string, FieldOfStudy[]> = {};
        markets.forEach((market, index) => {
            fieldsByCode[market.code] = fieldLists[index] || [];
        });

        setOptions({
            fieldOfOptionsCA: fieldsByCode.CA || [],
            fieldOfOptionsAU: fieldsByCode.AU || [],
            fieldOfOptionsUK: fieldsByCode.UK || [],
            countryOptions: countries || [''],
            educationOptions: educationnes || [''],
            regionOptions: regions || {},
        });
    };

    const [, setHasSpouse] = useState<boolean>(false);
    const handleSpouse = async (data: Spouse) => {
        if (data && Object.keys(data).length) {
            setHasSpouse(true);
        }
        if (data == null || data) {
            setIsSpouseInfo(true);
        } else {
            setIsSpouseInfo(false);
        }
    };

    useEffect(() => {
        ReactGA.send({
            hitType: 'pageview',
            page: `${window.location.pathname + '/' + tableName}`,
            title: 'EducationForm',
        });
    }, []);

    const { role } = useAuth();
    const params = useParams();
    const candidate_id = Number(params.id);

    const isSalesPerson = isSalesPersonRole(role);
    const { isAuthenticated } = useSelector((state: RootState) => state.auth);

    useEffect(() => {
        if (isAuthenticated) {
            setIsLoading(true);
            setIsUserData(false);
            setIsSpouseInfo(false);
            setData([initEntry]);
            if (isSalesPerson) {
                api.tableInput.fetchCandidateInputSales(loadData, tableName, candidate_id);
                api.tableInput.fetchCandidateInputSales(handleSpouse, 'Spouse', candidate_id);
            } else {
                api.tableInput.fetchCandidateInput(loadData, tableName);
                api.tableInput.fetchCandidateInput(handleSpouse, 'Spouse');
            }
        } else {
            setIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [candidate_id, isAuthenticated]);

    const studyMessages = (entry: Entry) =>
        fieldOfStudyValidationMessages(codes, entry, t('requiredField', { lng: language }));

    const validate = () => {
        let validated = true;
        const temp = [...validationMsg];
        for (let i = 0; i < data.length; i++) {
            const studyMsgs = studyMessages(data[i]);
            if (Object.values(studyMsgs).some(Boolean)) {
                validated = false;
            }
            for (const key of Object.keys(initEntry)) {
                const value = data[i][key as keyof Entry];
                let msg = '';
                if (
                    key === 'region' &&
                    !['Australia', 'Canada', 'United Kingdom'].includes(data[i].country as string)
                )
                    continue;
                if (key === 'language' || key === 'institution_name' || key === 'zip') continue;
                if (isFieldOfStudyIdKey(key)) continue;
                // End required only when completed and not current (matches v2 stepper).
                if (key === 'end' && (!data[i].completed || data[i].current)) continue;
                else if (isEmptyFieldValue(value)) {
                    msg = t('requiredField', { lng: language });
                    validated = false;
                }
                temp[i] = { ...temp[i], [key]: msg };
            }
            temp[i] = { ...temp[i], ...studyMsgs };
        }
        setValidationMsg(temp);
        return validated;
    };

    const validateSingleField = (
        idx: number,
        field: string,
        value: string | boolean | null | number
    ) => {
        const temp = [...validationMsg];
        let msg = '';
        if (value === '' || value === null) {
            msg = t('requiredField', { lng: language });
        }
        if (isFieldOfStudyIdKey(field) || field === 'type') {
            const nextEntry = { ...data[idx], [field]: value };
            temp[idx] = {
                ...temp[idx],
                ...(field === 'type' ? { type: msg } : {}),
                ...studyMessages(nextEntry),
            };
        } else {
            temp[idx] = { ...temp[idx], [field]: msg };
        }
        setValidationMsg(temp);
    };

    const handleUpdate = (idx: number, field: string, value: string | boolean | null | number) => {
        const temp = [...data];
        const tempDisableFieldOfStudy = [...disableFieldOfStudy];
        if (field === 'type' && value === 'High school') {
            tempDisableFieldOfStudy[idx] = true;
            setDisableFieldOfStudy(tempDisableFieldOfStudy);
            temp[idx] = {
                ...data[idx],
                [field]: value,
                field_of_study_au_id: null,
                field_of_study_ca_id: null,
                field_of_study_uk_id: null,
            };
            setData(temp);
            const tempMsg = [...validationMsg];
            tempMsg[idx] = {
                ...tempMsg[idx],
                type: '',
                field_of_study_au_id: '',
                field_of_study_ca_id: '',
                field_of_study_uk_id: '',
            };
            setValidationMsg(tempMsg);
            return;
        } else {
            tempDisableFieldOfStudy[idx] = false;
            setDisableFieldOfStudy(tempDisableFieldOfStudy);
        }
        validateSingleField(idx, field, value);
        if (field === 'country') {
            temp[idx] = {
                ...temp[idx],
                region: '',
            };
        }
        if (value === true) {
            temp[idx] = {
                ...temp[idx],
                current: field === 'completed' ? false : temp[idx].current,
                completed: field === 'current' ? false : temp[idx].completed,
                // Clear the end date when the education is marked ongoing.
                end: field === 'current' ? null : temp[idx].end,
            };
            if (field === 'current') {
                const tempMsg = [...validationMsg];
                tempMsg[idx] = { ...tempMsg[idx], end: '' };
                setValidationMsg(tempMsg);
            }
        }
        temp[idx] = { ...temp[idx], [field]: value };
        setData(temp);
    };

    const handleDateUpdate = (idx: number, field: string, newDate: Date | null) => {
        if (newDate === null) {
            const temp = [...validationMsg];
            temp[idx] = {
                ...temp[idx],
                [field]: t('requiredField', { lng: language }),
            };
            setValidationMsg(temp);
            const tmp = [...data];
            tmp[idx] = { ...tmp[idx], [field]: '' };
            setData(tmp);
            return;
        }
        if (!(newDate instanceof Date) || isNaN(newDate.getTime())) {
            const temp = [...validationMsg];
            temp[idx] = { ...temp[idx], [field]: 'Invalid date format' };
            setValidationMsg(temp);
            return;
        }

        const extractDateFormat = formatDateToYMD(newDate);

        if (!isFullDateFormat(newDate)) {
            // Incomplete year while typing — keep month visible
            const temp = [...validationMsg];
            temp[idx] = { ...temp[idx], [field]: 'Invalid date format' };
            setValidationMsg(temp);
            const tmp = [...data];
            tmp[idx] = { ...tmp[idx], [field]: extractDateFormat };
            setData(tmp);
            return;
        }

        const temp = [...validationMsg];
        temp[idx] = { ...temp[idx], [field]: '' };
        setValidationMsg(temp);
        const tmp = [...data];
        tmp[idx] = { ...data[idx], [field]: extractDateFormat };
        if (field === 'start') {
            tmp[idx] = { ...tmp[idx], end: null };
        }
        setData(tmp);
    };

    const handleAddEntry = () => {
        if (data.length > 10) return;
        const newEntry: Entry = initEntry;

        const temp: Entry[] = [...data, newEntry];
        setData(temp);

        const tempMsg: Msg[] = [...validationMsg, initMsg];
        setValidationMsg(tempMsg);
    };

    const handleDeleteEntry = (idx: number) => {
        const temp: Entry[] = [...data];
        temp.splice(idx, 1);
        setData(temp);
        const tempMsg: Msg[] = [...validationMsg];
        tempMsg.splice(idx, 1);
        setValidationMsg(tempMsg);
    };

    function handleSwitchPartnerMe() {
        if (validate()) {
            if (isSalesPerson) {
                api.tableInput.postSales(data, tableName, candidate_id);
            } else {
                api.tableInput.post(data, tableName);
            }
            dispatch(setShowSpouse());
        }
    }

    const checkEndDateBeforeStart = (
        endDate: Date | null,
        startDate: Date | null | undefined,
        idx: number
    ) => {
        const normalizedStartDate = startDate ? startOfDay(new Date(startDate)) : undefined;
        const normalizedEndDate = endDate ? startOfDay(endDate) : undefined;

        if (normalizedEndDate && normalizedStartDate && normalizedEndDate < normalizedStartDate) {
            const temp = [...validationMsg];
            temp[idx] = {
                ...temp[idx],
                end: t('endDateBeforeStartDate', { lng: language }),
            };
            setValidationMsg(temp);
            return true;
        }

        return false;
    };

    const [ukInstitutionOptions, setUkInstitutionOptions] = useState<string[]>([]);
    const [caInstitutionOptions, setCaInstitutionOptions] = useState<string[]>([]);
    const [auInstitutionOptions, setAuInstitutionOptions] = useState<string[]>([]);

    const getUkInstitutionOptions = () =>
        fetchOptions(api?.assessment_options.getUkInstitutionOptions, setUkInstitutionOptions);

    const getCaInstitutionOptions = () =>
        fetchOptions(api?.assessment_options.getCaInstitutionOptions, setCaInstitutionOptions);

    const getAuInstitutionOptions = () =>
        fetchOptions(api?.assessment_options.getAuInstitutionOptions, setAuInstitutionOptions);

    const ukEnabled = has('UK');
    const caEnabled = has('CA');
    const auEnabled = has('AU');

    useEffect(() => {
        if (!ukEnabled) return;
        getUkInstitutionOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ukEnabled]);

    useEffect(() => {
        if (!caEnabled) return;
        getCaInstitutionOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caEnabled]);

    useEffect(() => {
        if (!auEnabled) return;
        getAuInstitutionOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auEnabled]);

    const [openIndices, setOpenIndices] = useState<boolean[]>([]);

    const handleCloseJobTitleSelector = (index: number) => {
        const newOpenIndices = [...openIndices];
        newOpenIndices[index] = false;
        setOpenIndices(newOpenIndices);
    };

    useEffect(() => {
        setOpenIndices(data.map(() => false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.length]);

    return (
        <>
            {isLoading ? (
                <S.LoadingContainer>
                    <CircularProgress color="primary" />
                </S.LoadingContainer>
            ) : (
                <FormBox validation={validate} data={[data]} tableNames={[tableName]}>
                    <S.TitleContainer>
                        <div>
                            <S.Title style={{ marginBottom: '0.5rem' }}>
                                {t('education', { lng: language })}
                            </S.Title>
                            <S.FormNote>{t('educationNote', { lng: language })}</S.FormNote>
                        </div>
                        <PartnerInfoSwitch partnerActive={false} onSwitch={handleSwitchPartnerMe} />
                    </S.TitleContainer>
                    {data.map((_, i) => {
                        const studyMarkets = visibleFieldOfStudyMarkets(codes, data[i].type);
                        return (
                            <S.EntryCard key={i}>
                                <S.SubTitle>
                                    <>{`${t('education', { lng: language })} ${i + 1}`}</>
                                    <S.DeleteButton hidden={i === 0}>
                                        <button
                                            onClick={() => {
                                                handleDeleteEntry(i);
                                            }}
                                        >
                                            <FaTrash />
                                        </button>
                                    </S.DeleteButton>
                                </S.SubTitle>

                                <div>
                                    <S.GridSection>
                                        <S.FlexColumn>
                                            <Autocomplete
                                                disablePortal
                                                id="Type"
                                                value={data[i].type}
                                                options={options.educationOptions}
                                                onChange={(_, v: string | null) =>
                                                    handleUpdate(i, 'type', v)
                                                }
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label={`*${t('type', { lng: language })}`}
                                                        error={Boolean(validationMsg[i].type)}
                                                    />
                                                )}
                                            />
                                            <S.ValidationMsg>
                                                {validationMsg[i].type}
                                            </S.ValidationMsg>
                                        </S.FlexColumn>
                                        {studyMarkets.showCa && (
                                            <S.FlexColumn>
                                                <Autocomplete
                                                    disablePortal
                                                    disabled={disableFieldOfStudy[i]}
                                                    id="field_of_study_ca_id"
                                                    value={
                                                        options.fieldOfOptionsCA.find(
                                                            (item) =>
                                                                item.id ===
                                                                Number(data[i].field_of_study_ca_id)
                                                        ) || null
                                                    }
                                                    options={options.fieldOfOptionsCA}
                                                    getOptionLabel={(option) => option.name}
                                                    onChange={(_, selectedOption) =>
                                                        handleUpdate(
                                                            i,
                                                            'field_of_study_ca_id',
                                                            selectedOption?.id || null
                                                        )
                                                    }
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label={`*${t('fieldOfStudyCanada', { lng: language })}`}
                                                            error={Boolean(
                                                                validationMsg[i]
                                                                    .field_of_study_ca_id
                                                            )}
                                                        />
                                                    )}
                                                />
                                                <S.ValidationMsg>
                                                    {validationMsg[i].field_of_study_ca_id}
                                                </S.ValidationMsg>
                                            </S.FlexColumn>
                                        )}
                                        {studyMarkets.showUk && (
                                            <S.FlexColumn>
                                                <Autocomplete
                                                    disablePortal
                                                    disabled={disableFieldOfStudy[i]}
                                                    id="field_of_study_uk_id"
                                                    value={
                                                        options.fieldOfOptionsUK.find(
                                                            (item) =>
                                                                item.id ===
                                                                Number(data[i].field_of_study_uk_id)
                                                        ) || null
                                                    }
                                                    options={options.fieldOfOptionsUK}
                                                    getOptionLabel={(option) => option.name}
                                                    onChange={(_, selectedOption) =>
                                                        handleUpdate(
                                                            i,
                                                            'field_of_study_uk_id',
                                                            selectedOption?.id || null
                                                        )
                                                    }
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label={`*${t('fieldOfStudyUnitedKingdom', { lng: language })}`}
                                                            error={Boolean(
                                                                validationMsg[i]
                                                                    .field_of_study_uk_id
                                                            )}
                                                        />
                                                    )}
                                                />
                                                <S.ValidationMsg>
                                                    {validationMsg[i].field_of_study_uk_id}
                                                </S.ValidationMsg>
                                            </S.FlexColumn>
                                        )}
                                        <S.FlexColumn>
                                            <Autocomplete
                                                disablePortal
                                                id="country"
                                                value={data[i].country}
                                                options={options.countryOptions}
                                                onChange={(_, value: string | null) =>
                                                    handleUpdate(i, 'country', value)
                                                }
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label={`*${t('country', {
                                                            lng: language,
                                                        })}`}
                                                        error={Boolean(validationMsg[i].country)}
                                                    />
                                                )}
                                            />
                                            <S.ValidationMsg>
                                                {validationMsg[i].country}
                                            </S.ValidationMsg>
                                        </S.FlexColumn>
                                        {[
                                            'Australia',
                                            'Australie',
                                            'Canada',
                                            'United Kingdom',
                                            'Royaume Uni',
                                        ].includes(data[i].country as string) && (
                                            <S.FlexColumn>
                                                <Autocomplete
                                                    disablePortal
                                                    id="region"
                                                    value={data[i].region}
                                                    options={
                                                        options.regionOptions[
                                                            data[i].country as string
                                                        ] || []
                                                    }
                                                    onChange={(_, v: string | null) =>
                                                        handleUpdate(i, 'region', v)
                                                    }
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label={`*${t('region', { lng: language })}`}
                                                            error={Boolean(validationMsg[i].region)}
                                                        />
                                                    )}
                                                />
                                                <S.ValidationMsg>
                                                    {validationMsg[i].region}
                                                </S.ValidationMsg>
                                            </S.FlexColumn>
                                        )}
                                        {rowCountryEnabled(codes, data[i].country, 'AU') && (
                                            <BaseInput
                                                id="Zip"
                                                label={t('zipLabel', { lng: language })}
                                                type="text"
                                                value={data[i].zip}
                                                onChange={(e) => {
                                                    handleUpdate(i, 'zip', e.target.value);
                                                }}
                                                slotProps={{
                                                    input: {
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <Tooltip
                                                                    title={t(
                                                                        'zipTooltipEducation',
                                                                        { lng: language }
                                                                    )}
                                                                    arrow
                                                                    enterTouchDelay={0}
                                                                    leaveTouchDelay={6000}
                                                                >
                                                                    <InfoOutlinedIcon
                                                                        sx={{
                                                                            color: '#686868',
                                                                            cursor: 'pointer',
                                                                            fontSize: 20,
                                                                        }}
                                                                    />
                                                                </Tooltip>
                                                            </InputAdornment>
                                                        ),
                                                    },
                                                }}
                                            />
                                        )}
                                        {hasSelfServeMarket && (
                                            <S.FlexColumn>
                                                <Autocomplete
                                                    disablePortal
                                                    id="language"
                                                    value={data[i].language}
                                                    options={LANGUAGES}
                                                    onChange={(_, v: string | null) => {
                                                        handleUpdate(i, 'language', v);
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label={`${t('language', { lng: language })}`}
                                                            error={validationMsg[i].language !== ''}
                                                        />
                                                    )}
                                                />
                                                <S.ValidationMsg>
                                                    {validationMsg[i].language}
                                                </S.ValidationMsg>
                                            </S.FlexColumn>
                                        )}
                                        {rowCountryEnabled(codes, data[i].country, 'UK') && (
                                            <S.FlexColumn>
                                                <Autocomplete
                                                    disablePortal
                                                    id="institution_name"
                                                    value={data[i].institution_name}
                                                    options={ukInstitutionOptions}
                                                    onChange={(_, v: string | null) => {
                                                        handleUpdate(i, 'institution_name', v);
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label={`${t('institution', { lng: language })}`}
                                                            error={
                                                                validationMsg[i]
                                                                    .institution_name !== ''
                                                            }
                                                        />
                                                    )}
                                                />
                                                <S.ValidationMsg>
                                                    {validationMsg[i].institution_name}
                                                </S.ValidationMsg>
                                            </S.FlexColumn>
                                        )}
                                        {rowCountryEnabled(codes, data[i].country, 'CA') && (
                                            <S.FlexColumn>
                                                <Autocomplete
                                                    disablePortal
                                                    id="institution_name"
                                                    value={data[i].institution_name}
                                                    options={caInstitutionOptions}
                                                    onChange={(_, v: string | null) => {
                                                        handleUpdate(i, 'institution_name', v);
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label={`${t('institution', { lng: language })}`}
                                                            error={
                                                                validationMsg[i]
                                                                    .institution_name !== ''
                                                            }
                                                        />
                                                    )}
                                                />
                                                <S.ValidationMsg>
                                                    {validationMsg[i].institution_name}
                                                </S.ValidationMsg>
                                            </S.FlexColumn>
                                        )}
                                        {rowCountryEnabled(codes, data[i].country, 'AU') && (
                                            <S.FlexColumn>
                                                <Autocomplete
                                                    disablePortal
                                                    id="institution_name"
                                                    value={data[i].institution_name}
                                                    options={auInstitutionOptions}
                                                    onChange={(_, v: string | null) => {
                                                        handleUpdate(i, 'institution_name', v);
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label={`${t('institution', { lng: language })}`}
                                                            error={
                                                                validationMsg[i]
                                                                    .institution_name !== ''
                                                            }
                                                        />
                                                    )}
                                                />
                                                <S.ValidationMsg>
                                                    {validationMsg[i].institution_name}
                                                </S.ValidationMsg>
                                            </S.FlexColumn>
                                        )}
                                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                                            <DesktopDatePicker
                                                views={['year', 'month']}
                                                minDate={new Date(EARLIEST_EDUCATION_DATE)}
                                                maxDate={new Date()}
                                                label={`*${t('startDate', { lng: language })}`}
                                                format="MM/yyyy"
                                                openTo="year"
                                                value={parseStoredDate(data[i].start)}
                                                onChange={(date: Date | null) => {
                                                    handleDateUpdate(i, 'start', date);
                                                }}
                                                slotProps={{
                                                    field: { clearable: true },
                                                    textField: {
                                                        error: validationMsg[i].start !== '',
                                                        helperText: validationMsg[i].start,
                                                    },
                                                }}
                                            />
                                        </LocalizationProvider>

                                        <ToggleButtonGroup
                                            options={[
                                                t('yes', { lng: language }),
                                                t('no', { lng: language }),
                                            ]}
                                            name="current"
                                            label={t('ongoing', { lng: language })}
                                            value={
                                                data[i].current === undefined ||
                                                data[i].current === null
                                                    ? undefined
                                                    : data[i].current
                                                      ? t('yes', { lng: language })
                                                      : t('no', { lng: language })
                                            }
                                            onChange={(arg1: string, arg2: string) =>
                                                handleUpdate(
                                                    i,
                                                    arg1,
                                                    arg2 === t('yes', { lng: language })
                                                )
                                            }
                                            error={validationMsg[i].current}
                                        />
                                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                                            <DesktopDatePicker
                                                views={['year', 'month']}
                                                minDate={
                                                    parseStoredDate(data[i].start) ??
                                                    new Date(EARLIEST_EDUCATION_DATE)
                                                }
                                                maxDate={new Date()}
                                                disabled={data[i].current}
                                                label={`*${t('endDate', { lng: language })}`}
                                                openTo="year"
                                                format="MM/yyyy"
                                                value={parseStoredDate(data[i].end)}
                                                onChange={(date: Date | null) => {
                                                    // Commit typed value first so the field stays in sync
                                                    // (early return left partial years e.g. 0201 on screen).
                                                    handleDateUpdate(i, 'end', date);
                                                    if (date && isFullDateFormat(date)) {
                                                        checkEndDateBeforeStart(
                                                            date,
                                                            parseStoredDate(data[i].start),
                                                            i
                                                        );
                                                    }
                                                }}
                                                slotProps={{
                                                    field: { clearable: true },
                                                    textField: {
                                                        error: validationMsg[i].end !== '',
                                                        helperText: validationMsg[i].end,
                                                    },
                                                }}
                                            />
                                        </LocalizationProvider>
                                        <ToggleButtonGroup
                                            options={[
                                                t('yes', { lng: language }),
                                                t('no', { lng: language }),
                                            ]}
                                            name="completed"
                                            label={t('completed', { lng: language })}
                                            value={
                                                data[i].completed === undefined ||
                                                data[i].completed === null
                                                    ? undefined
                                                    : data[i].completed
                                                      ? t('yes', { lng: language })
                                                      : t('no', { lng: language })
                                            }
                                            onChange={(arg1: string, arg2: string) =>
                                                handleUpdate(
                                                    i,
                                                    arg1,
                                                    arg2 === t('yes', { lng: language })
                                                )
                                            }
                                            error={validationMsg[i].completed}
                                        />
                                    </S.GridSection>
                                    {studyMarkets.showAu && (
                                        <S.GridSection>
                                            <Autocomplete
                                                disablePortal
                                                disabled={disableFieldOfStudy[i]}
                                                id="field_of_study_au_id"
                                                value={
                                                    options.fieldOfOptionsAU.find(
                                                        (item) =>
                                                            item.id ===
                                                            Number(data[i].field_of_study_au_id)
                                                    ) || null
                                                }
                                                options={options.fieldOfOptionsAU}
                                                getOptionLabel={(option) => option.name}
                                                onChange={(_, selectedOption) =>
                                                    handleUpdate(
                                                        i,
                                                        'field_of_study_au_id',
                                                        selectedOption?.id || null
                                                    )
                                                }
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label={`*${t('fieldOfStudyAustralia', { lng: language })}`}
                                                        error={Boolean(
                                                            validationMsg[i].field_of_study_au_id
                                                        )}
                                                    />
                                                )}
                                            />

                                            <Button
                                                color="purple"
                                                border="purple"
                                                onClick={() => {
                                                    const newOpenIndices = openIndices.slice();
                                                    newOpenIndices[i] = true;
                                                    setOpenIndices(newOpenIndices);
                                                }}
                                                style={{
                                                    width: '136px',
                                                    height: '56px',
                                                }}
                                            >
                                                {language === 'EN' && (
                                                    <AIIcon
                                                        style={{
                                                            width: '24px',
                                                            height: '24px',
                                                        }}
                                                    />
                                                )}
                                                {t('aiAssistant', { lng: language })}
                                            </Button>
                                            <AIRecommendedFieldOfStudy
                                                open={openIndices[i] || false}
                                                handleClose={() => handleCloseJobTitleSelector(i)}
                                                handleUpdate={(index, field, value) =>
                                                    handleUpdate(index, field, value)
                                                }
                                                index={i}
                                                countryCode="AU"
                                                country={data[i].country}
                                                jobTitle={options.fieldOfOptionsAU.find(
                                                    (option) =>
                                                        option?.id === data[i].field_of_study_au_id
                                                )}
                                                options={options.fieldOfOptionsAU}
                                                handleRecommendedJobs={undefined}
                                            />

                                            <S.ValidationMsg>
                                                {validationMsg[i].field_of_study_au_id}
                                            </S.ValidationMsg>
                                        </S.GridSection>
                                    )}
                                </div>
                            </S.EntryCard>
                        );
                    })}
                    <AddEntryFooter
                        title={t('addEducation', { lng: language })}
                        addLabel={t('addEducation', { lng: language })}
                        addAnotherLabel={t('addAnotherEducation', { lng: language })}
                        hasEntries={data.length > 0}
                        onClick={() => handleAddEntry()}
                    />
                </FormBox>
            )}
        </>
    );
}
